import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useSmartBack } from '../../hooks/useSmartBack';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  ArrowLeft, Loader2, Clock, Search, MapPin, AlertTriangle, CheckCircle2,
  Download, ChevronRight, Pencil, Save
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Separator } from '../../components/ui/separator';
import { Label } from '../../components/ui/label';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import RecordActions from '../../components/RecordActions';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminTimeTrackingPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { back } = useSmartBack('/admin/dashboard');
  const [entries, setEntries] = useState([]);
  const [flagged, setFlagged] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('all');
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    try {
      const [entriesRes, flaggedRes] = await Promise.all([
        axios.get(`${API_URL}/api/time-entries`, { headers }),
        axios.get(`${API_URL}/api/time-entries/flagged`, { headers }).catch(() => ({ data: [] })),
      ]);
      setEntries(entriesRes.data || []);
      setFlagged(flaggedRes.data || []);
    } catch { toast.error('Failed to load time entries'); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleOverrideGPS = async (entryId) => {
    try {
      await axios.post(`${API_URL}/api/time-entries/${entryId}/override-gps`, {}, { headers });
      toast.success('GPS flag overridden');
      fetchData();
      setDetailOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to override'); }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/time-entries/export`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'time_entries.csv'; a.click();
      toast.success('CSV downloaded');
    } catch { toast.error('Failed to export'); }
  };

  const displayEntries = tab === 'flagged' ? flagged : entries;
  const filtered = displayEntries.filter(e => {
    if (!search) return true;
    const t = search.toLowerCase();
    return (e.cleaner_name || '').toLowerCase().includes(t) || (e.id || '').toLowerCase().includes(t);
  });

  const formatDuration = (mins) => {
    if (!mins) return '-';
    const h = Math.floor(mins / 60);
    const m = Math.round(mins % 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" /></div>;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-time-tracking-page">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center h-16 gap-4">
          <Button variant="ghost" size="icon" onClick={back} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
          <div className="flex-1"><h1 className="text-xl font-heading text-zinc-900">Time Tracking</h1><p className="text-sm text-zinc-500">{entries.length} entries, {flagged.length} flagged</p></div>
          <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-btn"><Download className="h-4 w-4 mr-2" />Export CSV</Button>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        <div className="flex gap-3 flex-wrap items-center">
          <Tabs value={tab} onValueChange={setTab} className="flex-shrink-0">
            <TabsList>
              <TabsTrigger value="all" data-testid="tab-all">All ({entries.length})</TabsTrigger>
              <TabsTrigger value="flagged" data-testid="tab-flagged">
                Flagged ({flagged.length})
                {flagged.length > 0 && <span className="ml-1 w-2 h-2 rounded-full bg-red-500 inline-block" />}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input placeholder="Search by cleaner name..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="search-input" />
          </div>
        </div>

        {filtered.length === 0 ? (
          <Card className="border-0 shadow-sm"><CardContent className="p-12 text-center"><Clock className="h-12 w-12 text-zinc-300 mx-auto mb-3" /><p className="text-zinc-500">No time entries found</p></CardContent></Card>
        ) : (
          <div className="space-y-2">
            {filtered.map((e, i) => (
              <motion.div key={e.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}>
                <Card className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => { setSelectedEntry(e); setDetailOpen(true); }} data-testid={`entry-${e.id}`}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${e.location_verified === false ? 'bg-red-50' : 'bg-blue-50'}`}>
                      {e.location_verified === false ? <AlertTriangle className="h-5 w-5 text-red-500" /> : <Clock className="h-5 w-5 text-blue-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-zinc-900">{e.cleaner_name || 'Unknown Cleaner'}</p>
                      <p className="text-sm text-zinc-500">
                        {e.clock_in_at ? new Date(e.clock_in_at).toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                        {e.clock_out_at && ` - ${new Date(e.clock_out_at).toLocaleTimeString('en-US', { timeZone: 'America/Los_Angeles', hour: '2-digit', minute: '2-digit' })} PT`}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0" onClick={(ev) => ev.stopPropagation()}>
                      <span className="text-sm font-medium text-zinc-700">{formatDuration(e.total_minutes)}</span>
                      {e.location_verified === false ? (
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">GPS Flag</Badge>
                      ) : e.location_verified ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">Verified</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-zinc-100 text-zinc-500 text-xs">Active</Badge>
                      )}
                      <RecordActions
                        canEdit={true}
                        canArchive={false}
                        canDelete={true}
                        deleteUrl={`/api/time-entries/${e.id}`}
                        deleteConfirmTitle="Delete this time entry?"
                        deleteConfirmDescription="Permanently removes the entry. If the entry has been included in a finalized payout, deletion will be blocked."
                        onEdit={() => setEditEntry(e)}
                        onDeleted={fetchData}
                        headers={headers}
                        testIdPrefix="time-entry"
                        entityId={e.id}
                      />
                      <ChevronRight className="h-4 w-4 text-zinc-300" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Time Entry Details</DialogTitle>
            <DialogDescription>{selectedEntry?.cleaner_name || 'Cleaner'}</DialogDescription>
          </DialogHeader>
          {selectedEntry && (
            <div className="space-y-4 mt-2">
              <div className="flex items-center gap-2">
                {selectedEntry.location_verified === false ? (
                  <Badge className="bg-red-100 text-red-800">GPS Flagged</Badge>
                ) : selectedEntry.location_verified ? (
                  <Badge className="bg-green-100 text-green-800">GPS Verified</Badge>
                ) : (
                  <Badge className="bg-zinc-100 text-zinc-600">In Progress</Badge>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-zinc-400">Clock In</span><p className="font-medium">{selectedEntry.clock_in_at ? `${new Date(selectedEntry.clock_in_at).toLocaleString('en-US', { timeZone: 'America/Los_Angeles' })} PT` : 'N/A'}</p></div>
                <div><span className="text-zinc-400">Clock Out</span><p className="font-medium">{selectedEntry.clock_out_at ? `${new Date(selectedEntry.clock_out_at).toLocaleString('en-US', { timeZone: 'America/Los_Angeles' })} PT` : 'Active'}</p></div>
                {selectedEntry.original_values && <div className="col-span-2" data-testid="time-entry-original"><span className="text-zinc-400">Original record (before office correction)</span><p className="font-medium">{selectedEntry.original_values.clock_in_at ? new Date(selectedEntry.original_values.clock_in_at).toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }) : '—'} → {selectedEntry.original_values.clock_out_at ? new Date(selectedEntry.original_values.clock_out_at).toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }) : '—'} PT · {selectedEntry.original_values.total_minutes ?? '—'} min · corrected by {selectedEntry.corrected_by_name}: {selectedEntry.correction_reason}</p></div>}
                <div><span className="text-zinc-400">Duration</span><p className="font-medium">{formatDuration(selectedEntry.total_minutes)}</p></div>
                <div><span className="text-zinc-400">Break</span><p className="font-medium">{selectedEntry.break_minutes || 0} min</p></div>
              </div>
              {selectedEntry.location_verified === false && (
                <>
                  <Separator />
                  <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3">
                    <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-medium text-red-800">GPS Verification Failed</p>
                      <p className="text-red-700">Cleaner was outside the 150m radius when clocking in.</p>
                    </div>
                  </div>
                  <Button className="w-full bg-[#F56F05] hover:bg-[#C2410C] text-white" onClick={() => handleOverrideGPS(selectedEntry.id)} data-testid="override-gps-btn">
                    <CheckCircle2 className="h-4 w-4 mr-2" /> Override GPS Flag
                  </Button>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {editEntry && (
        <EditTimeEntryDialog
          entry={editEntry}
          headers={headers}
          onClose={() => setEditEntry(null)}
          onSaved={() => { setEditEntry(null); fetchData(); }}
        />
      )}
    </div>
  );
};

const PT = 'America/Los_Angeles';
const ptOffsetMin = (d) => { const m = d.toLocaleString('en-US', { timeZone: PT, timeZoneName: 'shortOffset' }).match(/GMT([+-]\d+)/); return m ? Number(m[1]) * 60 : -480; };
const toPTInput = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || isNaN(d.getTime())) return '';
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: PT, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(d).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
};
export const fromPTInput = (v) => { const utc = Date.parse(`${v}:00Z`); return new Date(utc - ptOffsetMin(new Date(utc)) * 60000).toISOString(); };

export const EditTimeEntryDialog = ({ entry, headers, onClose, onSaved }) => {
  const initial = useMemo(() => ({
    clock_in_at: toPTInput(entry.clock_in_at),
    clock_out_at: toPTInput(entry.clock_out_at),
    break_minutes: entry.break_minutes ?? 0,
    notes: entry.notes || '',
  }), [entry]);
  const [form, setForm] = useState({ ...initial, reason: '' });
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  useEffect(() => {
    axios.get(`${API_URL}/api/time-entries/${entry.id}/history`, { headers }).then((r) => setHistory(r.data.filter((e) => ['admin_edit', 'admin_create'].includes(e.event_type)))).catch(() => {});
  }, [entry.id, headers]);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    const payload = {};
    if (form.clock_in_at && form.clock_in_at !== initial.clock_in_at) payload.clock_in_at = fromPTInput(form.clock_in_at);
    if (form.clock_out_at && form.clock_out_at !== initial.clock_out_at) payload.clock_out_at = fromPTInput(form.clock_out_at);
    const brk = parseInt(form.break_minutes, 10) || 0;
    if (brk !== (initial.break_minutes || 0)) payload.break_minutes = brk;
    if (form.notes !== initial.notes) payload.notes = form.notes;
    if (!Object.keys(payload).length) { toast.error('Nothing changed'); return; }
    if (form.reason.trim().length < 3) { toast.error('Add a reason for this correction'); return; }
    const inAt = payload.clock_in_at || entry.clock_in_at;
    const outAt = payload.clock_out_at || entry.clock_out_at;
    if (inAt && outAt && (payload.clock_in_at || payload.clock_out_at || 'break_minutes' in payload)) {
      payload.total_minutes = Math.max(0, Math.round((new Date(outAt) - new Date(inAt)) / 60000) - brk);
    }
    payload.reason = form.reason.trim();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/time-entries/${entry.id}`, payload, { headers });
      toast.success('Time entry updated');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="edit-time-entry-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-4 w-4 text-[#F56F05]" /> Edit Time Entry
          </DialogTitle>
          <DialogDescription data-testid="edit-time-entry-cleaner">Correcting {entry.cleaner_name || 'this Guru'}'s time. The original record, your name, the time and the reason are kept in the history.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs">Clock In (Pacific time)</Label>
            <Input type="datetime-local" value={form.clock_in_at} onChange={e => upd('clock_in_at', e.target.value)} data-testid="edit-clock-in" />
          </div>
          <div>
            <Label className="text-xs">Clock Out (Pacific time)</Label>
            <Input type="datetime-local" value={form.clock_out_at} onChange={e => upd('clock_out_at', e.target.value)} data-testid="edit-clock-out" />
          </div>
          <div>
            <Label className="text-xs">Break (minutes)</Label>
            <Input type="number" min="0" value={form.break_minutes} onChange={e => upd('break_minutes', e.target.value)} data-testid="edit-break" />
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Input value={form.notes} onChange={e => upd('notes', e.target.value)} data-testid="edit-notes" />
          </div>
          <div>
            <Label className="text-xs">Reason for correction (required)</Label>
            <Input value={form.reason} onChange={e => upd('reason', e.target.value)} maxLength={500} placeholder="e.g. Guru forgot to clock out, confirmed by text" data-testid="edit-reason" />
          </div>
          {history.length > 0 && (
            <div className="text-xs text-muted-foreground space-y-1" data-testid="edit-time-entry-history">
              <p className="font-medium text-foreground">Correction history</p>
              {history.map((h) => (
                <p key={h.id}>{new Date(h.event_time).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', dateStyle: 'medium', timeStyle: 'short' })} PT · {h.actor_name || h.metadata_json?.corrected_by_name || 'Admin'} · {h.metadata_json?.reason || 'No reason recorded'}</p>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-2 pt-4 border-t mt-4">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button className="flex-1 bg-[#F56F05] hover:bg-[#C2410C] text-white" onClick={save} disabled={saving} data-testid="edit-time-entry-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdminTimeTrackingPage;
